import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
})

// Attach the stored JWT to every request. Login/accept-invite are the only
// endpoints that don't need one — sending a stale/absent header there is
// harmless since the server doesn't require auth on those two routes.
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// A 401 means the token is missing/invalid/expired — clear it and bounce to
// login. Using a hard navigation (not react-router) since this interceptor
// runs outside any component context.
api.interceptors.response.use(
  res => res,
  err => {
    if (err?.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(err)
  }
)

export interface HealthReport {
  TimestampUnix: number
  RegisteredEndpoints: number
  ActiveCalls: number
  CPUPercent: number
  MemoryPercent: number
  KamailioRunning: boolean
  GSMTrunkReachable: boolean
  RtpengineRunning: boolean
  UptimeSeconds: number
  DNSFailoverRunning: boolean
  // Result of the agent's own SIP OPTIONS ping to Device.PrimarySipURI —
  // only meaningful when that's set; both are zero-valued otherwise.
  PrimaryReachable: boolean
  PrimaryLatencyMs: number
}

export interface Device {
  ID: string
  Name: string
  Site: string
  SipDomain: string
  GsmTrunkURI: string
  // Extension of the ring group any inbound call from the cellular gateway
  // is routed to, regardless of dialed number; empty disables the feature.
  CellularInboundRingGroup: string
  // Customer's primary cloud UCaaS/SIP service — used only for the agent's
  // independent OPTIONS-ping reachability check (see HealthReport above),
  // never for routing or registration. Empty disables the check.
  PrimarySipURI: string
  CurrentVersion: string
  ConfigVersion: string
  Status: string
  LastHealth: HealthReport | null
  LastSeenUnix: number
  KamailioChildren: number
  ShmMemoryMB: number
  PkgMemoryMB: number
  // Result of the most recent config push actually reaching the device
  // (its ConfigAck) — null if no push has ever been acked. Distinct from
  // ConfigVersion, which only reflects the last successfully applied
  // version and gives no signal that a push failed.
  LastConfigApplySuccess: boolean | null
  LastConfigApplyError: string
  LastConfigApplyVersion: string
  LastConfigApplyUnix: number
  // Real local network interface names the agent reported (e.g. ["eth0"]) —
  // empty until the device has connected at least once with an agent
  // version that reports this.
  Interfaces: string[]
  // Total system RAM in MB, reported in DeviceHello — zero until the device
  // has connected with an agent version that reports this. Shown in the UI
  // so an operator can tell which hardware tier is actually deployed.
  TotalMemoryMB: number
  // Owning organization — null until explicitly assigned.
  OrgID: number | null
  // Platform + live-fetched customer domain this device syncs call queues
  // from — null/empty until explicitly linked. Only meaningful if the
  // device's org has a PlatformID/ResellerName link of its own (see
  // Organization) — that's what scopes the domain picker.
  PlatformID: number | null
  NetsapiensDomain: string
  // DNS-failover service config — see DNSFailoverConfig in the proto for
  // why this exists. Empty FQDN means the agent runs no DNS service. The
  // hostname and default resolvers are shared/global; a network segment can
  // override resolvers via NetworkSegment.DNSUpstreamResolvers. There's no
  // target-IP field — the agent always auto-detects its own address (per
  // segment, or its primary outbound address on a flat/single-NIC device).
  DNSFailoverFQDN: string
  DNSUpstreamResolvers: string[] | null
  // Set only while Status === "pending_approval" — a device that
  // self-registered via an org enrollment code and is waiting for an admin
  // to approve it. Helps disambiguate multiple devices arriving at once.
  EnrollmentMAC: string
  EnrollmentIP: string
  EnrollmentRequestedAt: string | null
  // Disabled means the agent has torn down Kamailio/rtpengine/DNS failover
  // entirely — see disableDevice/enableDevice. DisabledReason is "manual" |
  // "stale" | "org_deleted".
  Disabled: boolean
  DisabledReason: string
  DisabledAt: string | null
  // Whether Kamailio's SIP TLS listener is on for this device — added
  // alongside plaintext UDP/TCP, never a replacement. See
  // enableDeviceSipTLS/disableDeviceSipTLS/regenerateDeviceSipTLSCert.
  SipTlsEnabled: boolean
  // Whether every call (not just cross-segment ones) is routed through
  // rtpengine to bridge any RTP<->SRTP mismatch between legs. Only the
  // high-RAM/dual-NIC hardware tier supports this — see maxNetworkSegments.
  SrtpBridgingEnabled: boolean
  // Whether REGISTER requests must authenticate with SIP digest auth — off
  // by default (the existing zero-auth behavior). See
  // syncDeviceCredentials/enableDeviceSipAuth/disableDeviceSipAuth.
  SipAuthEnabled: boolean
}

export interface PerformanceConfig {
  KamailioChildren: number
  ShmMemoryMB: number
  PkgMemoryMB: number
}

export interface DNSFailoverConfig {
  FQDN: string
  UpstreamResolvers: string[]
}

export const updateDeviceDNSFailover = async (id: string, cfg: DNSFailoverConfig): Promise<void> => {
  await api.put(`/devices/${id}/dns-failover`, cfg)
}

export interface RingGroup {
  ID: string
  Extension: string
  Name: string
  MemberExtensions: string[]
  Strategy: number
  TimeoutSeconds: number
}

export interface GSMPattern {
  ID: string
  Pattern: string
  Description: string
  Enabled: boolean
  Priority: number
}

export interface NetworkSegment {
  Name: string
  VlanID: number
  Interface: string
  AddressCIDR: string
  Gateway: string
  Ordinal: number
  // Overrides the device-wide DNS-failover upstream resolvers for just this
  // segment (e.g. a guest VLAN whose firewall only permits a specific
  // internal resolver). Empty/null uses the device-wide default.
  DNSUpstreamResolvers: string[] | null
}

export interface Release {
  Version: string
  DownloadURL: string
  SHA256: string
  IsLatest: boolean
  PublishedAt: number
  Notes: string
}

export interface DeviceConfig {
  SipDomain: string
  GsmTrunkURI: string
  CellularInboundRingGroup: string
  PrimarySipURI: string
}

export interface PushConfigResult {
  queued: boolean
  config_version: string
  online: boolean
}

export interface PushUpdateResult {
  queued: boolean
  online: boolean
}

export type UpdateType = 'agent' | 'package' | 'os' | 'reboot'

// Devices
export const listDevices = async (site?: string): Promise<Device[]> => {
  const params = site ? { site } : {}
  const { data } = await api.get<Device[]>('/devices', { params })
  return data ?? []
}

export const getDevice = async (id: string): Promise<Device> => {
  const { data } = await api.get<Device>(`/devices/${id}`)
  return data
}

// getHealthHistory returns health snapshots covering the last `hours` hours
// (server retains up to 30 days — see healthHistoryRetention in store.go).
export const getHealthHistory = async (id: string, hours: number): Promise<HealthReport[]> => {
  const { data } = await api.get<HealthReport[]>(`/devices/${id}/health-history`, { params: { hours } })
  return data ?? []
}

export const createDevice = async (device: { ID: string; Name: string; Site: string; OrgID?: number }): Promise<Device> => {
  const { data } = await api.post<Device>('/devices', device)
  return data
}

export const updateDevice = async (id: string, patch: Partial<Device>): Promise<Device> => {
  const { data } = await api.put<Device>(`/devices/${id}`, patch)
  return data
}

// disableDevice tears down Kamailio/rtpengine/DNS failover on the device
// entirely (see server/agent's Manager.deactivate) — not just a portal-side
// label. reason defaults to "manual" server-side if omitted.
export const disableDevice = async (id: string, reason?: string): Promise<{ disabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/disable`, reason ? { Reason: reason } : undefined)
  return data
}

export const enableDevice = async (id: string): Promise<{ disabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/enable`)
  return data
}

// SIP TLS is added alongside Kamailio's existing plaintext UDP/TCP
// listeners, never a replacement — see agent/kamailio/renderer.go. The cert
// is issued by the server's CA on first enable (or on regenerate); phones
// trust it by importing the CA cert from caCertDownloadURL below.
export const enableDeviceSipTLS = async (id: string): Promise<{ enabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/sip-tls/enable`)
  return data
}

export const disableDeviceSipTLS = async (id: string): Promise<{ enabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/sip-tls/disable`)
  return data
}

export const regenerateDeviceSipTLSCert = async (id: string): Promise<{ enabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/sip-tls/regenerate`)
  return data
}

// Public, unauthenticated download of the CA's certificate (server/ca) —
// hand this to a reseller to import into phone provisioning templates as a
// trusted root, so phones validate a device's SIP TLS cert.
export const caCertDownloadURL = '/api/ca-cert'

// RTP/SRTP media bridging: routes every call (not just cross-segment ones)
// through rtpengine, which detects and bridges any RTP<->SRTP mismatch
// between legs itself. Only available on the high-RAM/dual-NIC hardware
// tier — see maxNetworkSegments below; enabling on a base-tier device 400s.
export const enableDeviceSrtpBridging = async (id: string): Promise<{ enabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/srtp-bridging/enable`)
  return data
}

export const disableDeviceSrtpBridging = async (id: string): Promise<{ enabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/srtp-bridging/disable`)
  return data
}

// SIP digest authentication: fetches each registerable identity's SIP
// password from the platform, computes an HA1 digest server-side, and
// stores only the HA1 — the plaintext password never reaches the device.
// Off by default; sync credentials before enabling, or REGISTER will fail
// for every identity with no synced credential.
export const syncDeviceCredentials = async (id: string): Promise<{ credentialsSynced: number }> => {
  const { data } = await api.post(`/devices/${id}/sync-credentials`)
  return data
}

// Runs `mtr` on the device to a target host (defaults server-side to this
// device's PrimarySipURI when target is omitted/empty) and returns the raw
// `mtr --report --json` output for the caller to parse. On-demand and
// ephemeral — not persisted, unlike HealthReport/health-history.
export interface TracerouteResult {
  success: boolean
  error: string
  reportJson: string
}
export const runDeviceTraceroute = async (id: string, target?: string): Promise<TracerouteResult> => {
  const { data } = await api.post(`/devices/${id}/traceroute`, { Target: target ?? '' })
  return data
}

export const enableDeviceSipAuth = async (id: string): Promise<{ enabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/sip-auth/enable`)
  return data
}

export const disableDeviceSipAuth = async (id: string): Promise<{ enabled: boolean; online: boolean }> => {
  const { data } = await api.post(`/devices/${id}/sip-auth/disable`)
  return data
}

export const updateDeviceConfig = async (id: string, cfg: DeviceConfig): Promise<void> => {
  await api.put(`/devices/${id}/config`, cfg)
}

export const updateDevicePerformance = async (id: string, perf: PerformanceConfig): Promise<void> => {
  await api.put(`/devices/${id}/performance`, perf)
}

export const pushUpdate = async (
  deviceId: string,
  updateType: UpdateType,
  opts?: { releaseVersion?: string; downloadUrl?: string; sha256?: string; packageSpec?: string },
): Promise<PushUpdateResult> => {
  const { data } = await api.post<PushUpdateResult>(`/devices/${deviceId}/push-update`, {
    update_type: updateType,
    release_version: opts?.releaseVersion ?? '',
    download_url: opts?.downloadUrl ?? '',
    sha256: opts?.sha256 ?? '',
    package_spec: opts?.packageSpec ?? '',
  })
  return data
}

export const createEnrollmentToken = async (deviceId: string): Promise<string> => {
  const { data } = await api.post<{ token: string; device_id: string }>('/enrollment-tokens', {
    device_id: deviceId,
  })
  return data.token
}

export const pushConfig = async (deviceId: string): Promise<PushConfigResult> => {
  const { data } = await api.post<PushConfigResult>(`/devices/${deviceId}/push-config`)
  return data
}

// Ring groups
export const listRingGroups = async (deviceId: string): Promise<RingGroup[]> => {
  const { data } = await api.get<RingGroup[]>(`/devices/${deviceId}/ring-groups`)
  return data ?? []
}

export const upsertRingGroup = async (deviceId: string, rg: Partial<RingGroup>): Promise<RingGroup> => {
  const id = rg.ID || 'new'
  const { data } = await api.put<RingGroup>(`/devices/${deviceId}/ring-groups/${id}`, rg)
  return data
}

export const deleteRingGroup = async (deviceId: string, rgId: string): Promise<void> => {
  await api.delete(`/devices/${deviceId}/ring-groups/${rgId}`)
}

// GSM patterns
export const listGSMPatterns = async (deviceId: string): Promise<GSMPattern[]> => {
  const { data } = await api.get<GSMPattern[]>(`/devices/${deviceId}/gsm-patterns`)
  return data ?? []
}

export const upsertGSMPattern = async (deviceId: string, p: Partial<GSMPattern>): Promise<GSMPattern> => {
  const id = p.ID || 'new'
  const { data } = await api.put<GSMPattern>(`/devices/${deviceId}/gsm-patterns/${id}`, p)
  return data
}

export const deleteGSMPattern = async (deviceId: string, patternId: string): Promise<void> => {
  await api.delete(`/devices/${deviceId}/gsm-patterns/${patternId}`)
}

// Network segments
export const listNetworkSegments = async (deviceId: string): Promise<NetworkSegment[]> => {
  const { data } = await api.get<NetworkSegment[]>(`/devices/${deviceId}/network-segments`)
  return data ?? []
}

export const upsertNetworkSegment = async (
  deviceId: string,
  seg: Partial<NetworkSegment>,
): Promise<NetworkSegment> => {
  const name = seg.Name || 'new'
  const { data } = await api.put<NetworkSegment>(`/devices/${deviceId}/network-segments/${name}`, seg)
  return data
}

export const deleteNetworkSegment = async (deviceId: string, name: string): Promise<void> => {
  await api.delete(`/devices/${deviceId}/network-segments/${name}`)
}

// Releases
export const listReleases = async (): Promise<Release[]> => {
  const { data } = await api.get<Release[]>('/releases')
  return data ?? []
}

export const publishRelease = async (release: Partial<Release>): Promise<Release> => {
  const { data } = await api.post<Release>('/releases', release)
  return data
}

// --- Accounts: organizations, users, platforms ---

export interface Organization {
  ID: number
  ParentID: number | null
  Name: string
  CreatedAt: string
  // This org's link to one reseller under one platform, live-fetched from
  // netsapiens rather than synced into a local table — see
  // updateOrganizationPlatformLink. PlatformID must belong to this org or
  // an ancestor of it.
  PlatformID: number | null
  ResellerID: string
  ResellerName: string
}

export interface AccountUser {
  ID: number
  OrgID: number
  Email: string
  Name: string
  IsSuperUser: boolean
  CanManageUsers: boolean
  CanManageDevices: boolean
  CanManagePlatforms: boolean
  Status: string // invited | active | disabled
  CreatedAt: string
  MFAEnabled: boolean
  MFAMethod: 'email' | 'totp' | '' // preferred/default challenge method
  MFATOTPEnabled: boolean
  MFAEmailEnabled: boolean
}

// HARDWARE_TIER_RAM_THRESHOLD_MB separates the base (2GB single-NIC)
// hardware tier from the segmented (8GB dual-NIC) tier — mirrors the
// server-side threshold in server/api/rest.go's maxNetworkSegments.
export const HARDWARE_TIER_RAM_THRESHOLD_MB = 3072

// maxNetworkSegments returns how many network segments a device's hardware
// tier supports — the base tier can still use a single tagged VLAN on its
// one interface, it's just limited to one segment (not zero), since two
// segments is what triggers rtpengine media relay, which the base tier
// isn't sized for. 0 (RAM not yet reported) is treated as unknown/
// permissive rather than restricted.
export function maxNetworkSegments(totalMemoryMB: number): number {
  return totalMemoryMB > 0 && totalMemoryMB < HARDWARE_TIER_RAM_THRESHOLD_MB ? 1 : 2
}

// PLATFORM_TYPES enumerates the UCaaS integrations we know how to sync
// (currently just netsapiens) — used to render Platform Type as a dropdown
// instead of free text.
export const PLATFORM_TYPES = ['netsapiens'] as const
export type PlatformType = (typeof PLATFORM_TYPES)[number]

// Read-only API scopes required per platform type, shown as a hint next to
// the API Key field so operators provision the key with the right access.
// No spaces after the commas — netsapiens' own API key generation form
// doesn't handle a space in this field, so a straight copy-paste of this
// list must work as-is.
export const PLATFORM_REQUIRED_SCOPES: Record<string, string> = {
  netsapiens: 'reseller,domain,agent,callqueue,device',
}

export interface Platform {
  ID: number
  OrgID: number
  Name: string
  PlatformType: string
  ServerFQDN: string
  APIKey: string
  // DNS-failover defaults every device synced under this platform inherits
  // unless it sets its own — see updatePlatformDNSDefaults.
  DNSFailoverFQDN: string
  DNSUpstreamResolvers: string[] | null
  CreatedAt: string
  // Receives a message if this platform's periodic ring-group/credential
  // sync fails — empty means no notification is sent, just logged.
  NotificationEmail: string
}

export const updatePlatformDNSDefaults = async (platformID: number, cfg: DNSFailoverConfig): Promise<void> => {
  await api.put(`/platforms/${platformID}/dns-failover-defaults`, cfg)
}

export const updatePlatformNotificationEmail = async (platformID: number, email: string): Promise<void> => {
  await api.put(`/platforms/${platformID}/notification-email`, { Email: email })
}

// updateOrganizationPlatformLink sets (or, with platformID undefined,
// clears) an org's platform+reseller link.
export const updateOrganizationPlatformLink = async (
  orgID: number,
  platformID: number | undefined,
  resellerID: string,
  resellerName: string
): Promise<void> => {
  await api.put(`/organizations/${orgID}`, { PlatformID: platformID, ResellerID: resellerID, ResellerName: resellerName })
}

// Reseller/Domain are live-fetched from netsapiens, not synced into a
// local table — see listPlatformResellers/listPlatformDomains.
export interface Reseller {
  ID: string
  Name: string
}

export interface NetsapiensDomain {
  Domain: string
  Reseller: string
  Description: string
}

export const listPlatformResellers = async (platformID: number): Promise<Reseller[]> => {
  const { data } = await api.get<Reseller[]>(`/platforms/${platformID}/resellers`)
  return data ?? []
}

// listPlatformDomains fetches domains live, filtered to one reseller
// (required — there's no "all domains" picker use case in this UI).
export const listPlatformDomains = async (platformID: number, resellerName: string): Promise<NetsapiensDomain[]> => {
  const { data } = await api.get<NetsapiensDomain[]>(`/platforms/${platformID}/domains`, { params: { reseller: resellerName } })
  return data ?? []
}

// LoginResult is one of three shapes: a completed login (token+user), a
// request for a second step when the account already has MFA enabled (see
// loginMFA), or — MFA is mandatory for every user — a request to enroll in
// MFA for the first time before login can complete (see the
// loginMFASetup*/loginVerifyMFASetup* functions below).
export interface LoginResult {
  token?: string
  user?: AccountUser
  mfaRequired?: boolean
  method?: 'email' | 'totp' // preferred/default challenge method
  totpEnabled?: boolean
  emailEnabled?: boolean
  mfaToken?: string
  emailWarning?: string
  mfaSetupRequired?: boolean
}

export const login = async (email: string, password: string): Promise<LoginResult> => {
  const { data } = await api.post('/login', { Email: email, Password: password })
  return data
}

// method lets the operator answer with whichever enabled method they have
// to hand — e.g. falling back to email after loginMFASendEmail — rather
// than only ever the account's preferred default.
export const loginMFA = async (mfaToken: string, code: string, method?: 'email' | 'totp'): Promise<{ token: string; user: AccountUser }> => {
  const { data } = await api.post('/login/mfa', { MFAToken: mfaToken, Code: code, Method: method })
  return data
}

// loginMFASendEmail requests (or re-sends) an email code during an
// already-enrolled MFA challenge — used both when email is the preferred
// method and as a fallback when the operator can't use their authenticator
// app (e.g. lost/wiped phone) but has email MFA enabled too.
export const loginMFASendEmail = async (mfaToken: string): Promise<{ sent: boolean; warning?: string }> => {
  const { data } = await api.post('/login/mfa/send-email', { MFAToken: mfaToken })
  return data
}

// --- Mandatory first-time MFA enrollment (no session exists yet — these
// take the pending mfaToken from a login response instead of a bearer
// header) ---

export const loginMFASetupTOTP = async (mfaToken: string): Promise<{ secret: string; otpauthUrl: string }> => {
  const { data } = await api.post('/login/mfa-setup/totp', { MFAToken: mfaToken })
  return data
}

export const loginVerifyMFASetupTOTP = async (mfaToken: string, code: string): Promise<{ token: string; user: AccountUser }> => {
  const { data } = await api.post('/login/mfa-setup/totp/verify', { MFAToken: mfaToken, Code: code })
  return data
}

export const loginMFASetupEmail = async (mfaToken: string): Promise<{ sent: boolean; warning?: string }> => {
  const { data } = await api.post('/login/mfa-setup/email', { MFAToken: mfaToken })
  return data
}

export const loginVerifyMFASetupEmail = async (mfaToken: string, code: string): Promise<{ token: string; user: AccountUser }> => {
  const { data } = await api.post('/login/mfa-setup/email/verify', { MFAToken: mfaToken, Code: code })
  return data
}

export interface MFAStatus {
  enabled: boolean
  method: 'email' | 'totp' | '' // preferred/default challenge method
  totpEnabled: boolean
  emailEnabled: boolean
}

export const getMFAStatus = async (): Promise<MFAStatus> => {
  const { data } = await api.get<MFAStatus>('/mfa/status')
  return data
}

export const setupTOTP = async (): Promise<{ secret: string; otpauthUrl: string }> => {
  const { data } = await api.post('/mfa/totp/setup')
  return data
}

export const verifyTOTP = async (code: string): Promise<void> => {
  await api.post('/mfa/totp/verify', { Code: code })
}

export const setupEmailMFA = async (): Promise<{ sent: boolean; warning?: string }> => {
  const { data } = await api.post('/mfa/email/setup')
  return data
}

export const verifyEmailMFA = async (code: string): Promise<void> => {
  await api.post('/mfa/email/verify', { Code: code })
}

export const disableMFA = async (password: string): Promise<void> => {
  await api.post('/mfa/disable', { Password: password })
}

// disableMFAMethod turns off just one method (method: 'totp' | 'email'),
// leaving another enabled method (if any) untouched.
export const disableMFAMethod = async (method: 'totp' | 'email', password: string): Promise<void> => {
  await api.post('/mfa/disable-method', { Method: method, Password: password })
}

// setMFAPreferredMethod switches which already-enabled method is
// challenged by default at login.
export const setMFAPreferredMethod = async (method: 'totp' | 'email'): Promise<void> => {
  await api.post('/mfa/preferred', { Method: method })
}

export const resetUserMFA = async (userId: number): Promise<void> => {
  await api.post(`/users/${userId}/mfa-reset`)
}

// EnrollmentCode is an org-scoped, reusable, revocable code a field tech
// enters into a device's local setup web UI to self-register it into this
// org — see server/store/enrollcodes.go.
export interface EnrollmentCode {
  ID: number
  OrgID: number
  Code: string
  Name: string
  CreatedByID: number | null
  CreatedAt: string
  RevokedAt: string | null
}

export const listEnrollmentCodes = async (): Promise<EnrollmentCode[]> => {
  const { data } = await api.get<EnrollmentCode[]>('/enrollment-codes')
  return data ?? []
}

export const createEnrollmentCode = async (name: string, orgID?: number): Promise<EnrollmentCode> => {
  const { data } = await api.post<EnrollmentCode>('/enrollment-codes', { Name: name, OrgID: orgID })
  return data
}

export const revokeEnrollmentCode = async (id: number): Promise<void> => {
  await api.post(`/enrollment-codes/${id}/revoke`)
}

// approveDevice finalizes a device that self-registered via an enrollment
// code and is sitting in "pending_approval" status — Name/Site are
// optional overrides for whatever the field tech entered locally.
export const approveDevice = async (deviceId: string, params?: { name?: string; site?: string }): Promise<void> => {
  await api.post(`/devices/${deviceId}/approve`, { Name: params?.name ?? '', Site: params?.site ?? '' })
}

export const rejectDevice = async (deviceId: string): Promise<void> => {
  await api.post(`/devices/${deviceId}/reject`)
}

export const acceptInvite = async (token: string, password: string): Promise<void> => {
  await api.post('/accept-invite', { Token: token, Password: password })
}

export const listOrganizations = async (): Promise<Organization[]> => {
  const { data } = await api.get<Organization[]>('/organizations')
  return data ?? []
}

// topLevel requests a genuinely parentless (root) org — only a super user
// can do this (see the REST handler for why). Omitting parentID with
// topLevel=false nests under the caller's own org, matching the server's
// default.
export const createOrganization = async (parentID: number | null, name: string, topLevel = false): Promise<Organization> => {
  const { data } = await api.post<Organization>('/organizations', { ParentID: parentID, Name: name, TopLevel: topLevel })
  return data
}

// OrgDeletionImpact previews what deleting an org's whole subtree would
// take with it — users/platforms/enrollment codes cascade-delete, devices
// block the delete instead (see server/store/accounts.go).
export interface OrgDeletionImpact {
  OrgIDs: number[]
  OrgNames: string[]
  UserCount: number
  DeviceCount: number
}

export const getOrgDeletionImpact = async (orgID: number): Promise<OrgDeletionImpact> => {
  const { data } = await api.get<OrgDeletionImpact>(`/organizations/${orgID}/deletion-impact`)
  return data
}

// deleteOrganization deletes orgID and every descendant org in one go —
// super-user only. Throws (via axios) with a 409 if any device is still
// assigned anywhere in the subtree.
export const deleteOrganization = async (orgID: number): Promise<{ deleted: boolean; orgsDeleted: number; usersDeleted: number }> => {
  const { data } = await api.delete(`/organizations/${orgID}`)
  return data
}

export const listUsers = async (): Promise<AccountUser[]> => {
  const { data } = await api.get<AccountUser[]>('/users')
  return data ?? []
}

export const inviteUser = async (params: {
  orgID?: number
  email: string
  name: string
  // Only honored server-side if the inviting caller is already a super
  // user — see the REST handler.
  isSuperUser?: boolean
  canManageUsers: boolean
  canManageDevices: boolean
  canManagePlatforms: boolean
}): Promise<{ user: AccountUser; emailWarning?: string }> => {
  const { data } = await api.post('/users', {
    OrgID: params.orgID,
    Email: params.email,
    Name: params.name,
    IsSuperUser: params.isSuperUser ?? false,
    CanManageUsers: params.canManageUsers,
    CanManageDevices: params.canManageDevices,
    CanManagePlatforms: params.canManagePlatforms,
  })
  return data
}

export const updateUser = async (
  id: number,
  patch: Partial<Pick<AccountUser, 'IsSuperUser' | 'CanManageUsers' | 'CanManageDevices' | 'CanManagePlatforms' | 'Status'>>
): Promise<AccountUser> => {
  const { data } = await api.patch<AccountUser>(`/users/${id}`, patch)
  return data
}

export const listPlatforms = async (): Promise<Platform[]> => {
  const { data } = await api.get<Platform[]>('/platforms')
  return data ?? []
}

export const createPlatform = async (params: {
  orgID?: number
  name: string
  platformType: string
  serverFQDN: string
  apiKey: string
}): Promise<Platform> => {
  const { data } = await api.post<Platform>('/platforms', {
    OrgID: params.orgID,
    Name: params.name,
    PlatformType: params.platformType,
    ServerFQDN: params.serverFQDN,
    APIKey: params.apiKey,
  })
  return data
}

// testPlatformConnection verifies a server/API-key combo works (by
// listing domains) before the operator commits to saving it. Throws (via
// axios) with a descriptive message on failure.
export const testPlatformConnection = async (serverFQDN: string, apiKey: string): Promise<void> => {
  await api.post('/platforms/test-connection', { ServerFQDN: serverFQDN, APIKey: apiKey })
}

// updatePlatform is a partial update — omitted fields keep their current
// value server-side. Changing ServerFQDN or APIKey re-verifies the
// connection server-side before saving (same requirement as createPlatform).
export const updatePlatform = async (id: number, params: {
  name?: string
  platformType?: string
  serverFQDN?: string
  apiKey?: string
}): Promise<Platform> => {
  const { data } = await api.put<Platform>(`/platforms/${id}`, {
    Name: params.name,
    PlatformType: params.platformType,
    ServerFQDN: params.serverFQDN,
    APIKey: params.apiKey,
  })
  return data
}

export const deletePlatform = async (id: number): Promise<void> => {
  await api.delete(`/platforms/${id}`)
}

export const syncDeviceQueues = async (deviceId: string): Promise<{ queuesSynced: number; parksSkipped: number }> => {
  const { data } = await api.post(`/devices/${deviceId}/sync-queues`)
  return data
}

export interface AuditLogEntry {
  ID: number
  CreatedAt: string
  ActorUserID: number | null
  ActorEmail: string
  OrgID: number | null
  Action: string
  TargetType: string
  TargetID: string
  Detail: string
  Success: boolean
  IPAddress: string
}

export const listAuditLog = async (limit = 200): Promise<AuditLogEntry[]> => {
  const { data } = await api.get<AuditLogEntry[]>('/audit-log', { params: { limit } })
  return data ?? []
}

export interface BulkOperationItem {
  DeviceID: string
  Status: 'pending' | 'sent' | 'failed'
  SentAt: string | null
  Online: boolean
  Error: string
}

// A rate-paced fleet-wide reboot/update — see server/store/bulkops.go.
// Dispatched one device at a time by a server-side background worker, so
// progress continues even after this browser tab closes; poll
// getBulkOperation to watch it advance.
export interface BulkOperation {
  ID: number
  OrgID: number
  CreatedByEmail: string
  UpdateType: 'agent' | 'package' | 'os' | 'reboot'
  ReleaseVersion: string
  DownloadURL: string
  SHA256: string
  PackageSpec: string
  PaceSeconds: number
  Status: 'running' | 'completed' | 'cancelled'
  NextItemIndex: number
  LastItemSentAt: string | null
  Items: BulkOperationItem[]
  CreatedAt: string
}

export const createBulkOperation = async (params: {
  deviceIDs: string[]
  updateType: BulkOperation['UpdateType']
  releaseVersion?: string
  downloadUrl?: string
  sha256?: string
  packageSpec?: string
  paceSeconds: number
}): Promise<BulkOperation> => {
  const { data } = await api.post<BulkOperation>('/bulk-operations', {
    DeviceIDs: params.deviceIDs,
    UpdateType: params.updateType,
    ReleaseVersion: params.releaseVersion ?? '',
    DownloadURL: params.downloadUrl ?? '',
    SHA256: params.sha256 ?? '',
    PackageSpec: params.packageSpec ?? '',
    PaceSeconds: params.paceSeconds,
  })
  return data
}

export const listBulkOperations = async (limit = 50): Promise<BulkOperation[]> => {
  const { data } = await api.get<BulkOperation[]>('/bulk-operations', { params: { limit } })
  return data ?? []
}

export const getBulkOperation = async (id: number): Promise<BulkOperation> => {
  const { data } = await api.get<BulkOperation>(`/bulk-operations/${id}`)
  return data
}

export const cancelBulkOperation = async (id: number): Promise<void> => {
  await api.post(`/bulk-operations/${id}/cancel`)
}

export default api
